(() => {
  "use strict";

  const root = document.querySelector("[data-vl-research-library]");
  if (!root || root.dataset.catalogReady === "true") return;
  root.dataset.catalogReady = "true";

  const state = {
    records: [],
    filtered: [],
    ingredient: "all",
    model: "all",
    sort: "catalog",
    visible: 10,
    openId: null,
  };

  const list = root.querySelector("#publication-list");
  const template = root.querySelector("#publication-template");
  const resultCount = root.querySelector("#result-count");
  const resultUnit = root.querySelector("#result-unit");
  const resultDescription = root.querySelector("#result-description");
  const loadMore = root.querySelector("#load-more");
  const loadMoreWrap = root.querySelector(".vl-ir-load-more-wrap");
  const emptyState = root.querySelector("#empty-state");
  const clearFilters = root.querySelector("#clear-filters");
  const ingredientFilter = root.querySelector(".vl-ir-ingredient-filter");
  const toolbar = root.querySelector("[data-toolbar]");
  const catalogBiasNote = root.querySelector(".vl-ir-catalog-bias-note");
  const methodTrigger = root.querySelector(".vl-ir-method-trigger");
  const methodPanel = root.querySelector("#catalog-method");
  const resetButtons = [...root.querySelectorAll("[data-reset-all]")];
  const selectControllers = {};
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const ingredientKeys = {
    Ginseng: "ginseng",
    "Lion's mane": "lions-mane",
    "Whole sesame oil": "sesame-oil",
  };

  const ingredientLabels = {
    Ginseng: "Ginseng",
    "Lion's mane": "Lion’s mane",
    "Whole sesame oil": "Whole sesame oil",
  };

  const safeText = (value) => String(value ?? "").trim();
  const scrollBehavior = () => (reduceMotion ? "auto" : "smooth");

  function replaceCatalogHash() {
    if (!window.location.hash.startsWith("#paper-")) return;
    const url = new URL(window.location.href);
    url.hash = "research-library";
    history.replaceState({ openId: null }, "", url);
  }

  function directionGroup(record) {
    const direction = safeText(record.direction);
    if (direction.startsWith("positive_as_reported")) return "favorable";
    if (direction.startsWith("mixed")) return "mixed";
    if (direction.startsWith("null")) return "null";
    if (direction.startsWith("adverse")) return "adverse";
    if (direction.startsWith("mechanistic")) return "mechanistic";
    return "unclear";
  }

  function directionLabel(record) {
    const labels = {
      positive_as_reported: "Favorable as reported",
      positive_as_reported_with_dose_nuance: "Favorable as reported · Dose nuance",
      positive_as_reported_with_endpoint_nuance: "Favorable as reported · Endpoint nuance",
      mixed_positive_inflammation_no_colonization_effect: "Mixed · No colonization effect",
      mixed_internally_inconsistent: "Mixed · Internally inconsistent",
      mixed_unclear: "Mixed / unclear",
      unclear_unverified: "Outcome unverified / unclear",
    };
    return labels[record.direction] || "Outcome unverified / unclear";
  }

  function modelLabel(record, compact = false) {
    if (record.studyType === "cell_or_ex_vivo") return compact ? "Cell / ex vivo" : `Cell / ex vivo · ${record.speciesOrSystem}`;

    const model = safeText(record.speciesOrSystem).toLowerCase();
    let species = "non-human animal";
    if (/mice|mouse/.test(model)) species = "mouse";
    else if (/rats|rat/.test(model)) species = "rat";
    else if (/broiler|chicken/.test(model)) species = "chicken";
    else if (/bullfrog/.test(model)) species = "bullfrog";
    else if (/gerbil/.test(model)) species = "gerbil";
    else if (/ferret/.test(model)) species = "ferret";
    return compact ? `Animal · ${species}` : `Animal · ${record.speciesOrSystem}`;
  }

  function focusTheme(record) {
    const focus = safeText(record.focus).toLowerCase();
    if (/nausea|vomit|emetic/.test(focus)) return "nausea and vomiting measures";
    if (/diarrhea|diarrhoea/.test(focus)) return "diarrhea and colon measures";
    if (/mucosa|gastric|ulcer|lesion/.test(focus)) return "gastric-tissue measures";
    if (/barrier|permeability|tight-junction|tight junction|teer|claudin|occludin|zo-1/.test(focus)) {
      return "intestinal-barrier measures";
    }
    if (/microbiota|mucin|short-chain|short chain/.test(focus)) return "intestinal and microbiota measures";
    if (/colon|colitis|histology|inflamm/.test(focus)) return "colon-tissue and inflammatory measures";
    if (/oxidative|apoptosis|dna|cell/.test(focus)) return "gastrointestinal-cell measures";
    return "gastrointestinal measures";
  }

  function neutralTitle(record) {
    const ingredient = ingredientLabels[record.ingredient] || record.ingredient;
    const material = /whole sesame/i.test(record.ingredient)
      ? "Whole sesame oil"
      : `${ingredient} material`;
    const system = record.studyType === "cell_or_ex_vivo" ? "a cell or ex vivo model" : "an animal model";
    return `${material} and ${focusTheme(record)} in ${system}`;
  }

  function materialLevel(record) {
    const material = safeText(record.materialStudied).toLowerCase();
    if (record.ingredient === "Whole sesame oil") return "Whole ingredient oil";
    if (/whole|freeze-dried fruiting-body|fruiting-body material/.test(material)) return "Whole ingredient material";
    if (/separate|independent|multi-arm|compared/.test(material)) return "Separately attributable ingredient arm";
    if (/extract|ferment|preparation|myceli|ethanolic|aqueous|cultured/.test(material)) return "Specific preparation or extract";
    return "Attributable ingredient material";
  }

  function compactContext(record) {
    return `Material: ${record.materialStudied} · Model: ${record.speciesOrSystem} · Key limit: ${record.specificLimits}`;
  }

  function summaryBasisTag(record) {
    if (record.integrityStatus?.includes("abstract_unavailable")) return "Bibliographic record only";
    if (record.integrityStatus?.includes("detail_limited")) return "Summary basis: limited source record";
    return null;
  }

  function summaryBasis(record) {
    if (record.integrityStatus?.includes("abstract_unavailable")) {
      return "Bibliographic record only; outcome summary withheld pending full-text review.";
    }
    if (record.integrityStatus?.includes("source_detail_limited")) {
      return `${record.sourceType} record with limited source detail.`;
    }
    if (record.integrityStatus?.includes("material_detail_limited")) {
      return `${record.sourceType} record; material detail is limited in the indexed abstract.`;
    }
    return `${record.sourceType} publication record.`;
  }

  function catalogStatus(record) {
    if (record.integrityStatus?.includes("abstract_unavailable")) {
      return "Catalog status: citation and eligibility checked; outcome not independently reviewed";
    }
    if (record.integrityStatus?.includes("internal_result_conflict")) {
      return "Catalog status: citation and eligibility checked; internal result conflict flagged";
    }
    return "Catalog status: citation and eligibility checked";
  }

  function applyFilters() {
    state.filtered = state.records.filter((record) => {
      const ingredientMatch = state.ingredient === "all" || ingredientKeys[record.ingredient] === state.ingredient;
      const modelMatch =
        state.model === "all" ||
        (state.model === "animal" && record.studyType === "animal") ||
        (state.model === "cell-ex-vivo" && record.studyType === "cell_or_ex_vivo");
      return ingredientMatch && modelMatch;
    });

    if (state.sort === "newest") {
      state.filtered.sort((a, b) => b.year - a.year || a.id.localeCompare(b.id, undefined, { numeric: true }));
    } else if (state.sort === "oldest") {
      state.filtered.sort((a, b) => a.year - b.year || a.id.localeCompare(b.id, undefined, { numeric: true }));
    } else {
      state.filtered.sort((a, b) => state.records.indexOf(a) - state.records.indexOf(b));
    }
  }

  function makeTag(text, direction) {
    const tag = document.createElement("span");
    tag.textContent = text;
    if (direction) tag.dataset.direction = direction;
    return tag;
  }

  function renderRecord(record, index) {
    const fragment = template.content.cloneNode(true);
    const article = fragment.querySelector("[data-publication]");
    const detail = article.querySelector(".vl-ir-publication__detail");
    const detailTrigger = article.querySelector(".vl-ir-detail-trigger");
    const tags = article.querySelector(".vl-ir-publication__tags");
    const sourceLink = article.querySelector(".vl-ir-source-link");
    const catalogIndex = String(state.records.indexOf(record) + 1).padStart(2, "0");

    article.id = `paper-${record.id}`;
    article.dataset.id = record.id;
    article.querySelector(".vl-ir-publication__number").textContent = catalogIndex;
    article.querySelector(".vl-ir-publication__title").textContent = neutralTitle(record);
    article.querySelector(".vl-ir-publication__citation").textContent = `${record.authors} · ${record.journal} · ${record.year}`;
    article.querySelector(".vl-ir-publication__summary").textContent = record.summary;
    article.querySelector('[data-field="material-context"]').textContent = compactContext(record);

    tags.append(
      makeTag(ingredientLabels[record.ingredient] || record.ingredient),
      makeTag(modelLabel(record, true)),
      makeTag(directionLabel(record), directionGroup(record)),
    );
    const basisTag = summaryBasisTag(record);
    if (basisTag) tags.append(makeTag(basisTag, "unclear"));

    sourceLink.href = record.sourceUrl;
    sourceLink.setAttribute("aria-label", `Open original publication for catalog record ${record.id}`);

    const detailId = `detail-${record.id}`;
    detail.id = detailId;
    detailTrigger.setAttribute("aria-controls", detailId);
    detailTrigger.setAttribute("aria-label", `View details for catalog record ${record.id}`);

    article.querySelector('[data-field="material"]').textContent = record.materialStudied;
    article.querySelector('[data-field="material-level"]').textContent = materialLevel(record);
    article.querySelector('[data-field="model"]').textContent = modelLabel(record);
    article.querySelector('[data-field="focus"]').textContent = record.focus;
    article.querySelector('[data-field="reported"]').textContent = record.summary;
    article.querySelector('[data-field="direction"]').textContent = directionLabel(record);
    article.querySelector('[data-field="relevance"]').textContent = `Included because it used an attributable ${ingredientLabels[record.ingredient] || record.ingredient} material and measured the listed gastrointestinal endpoints.`;
    article.querySelector('[data-field="source-record"]').textContent = `${record.sourceType}${record.doi ? ` · DOI ${record.doi}` : " · No DOI listed"}`;
    article.querySelector('[data-field="summary-basis"]').textContent = summaryBasis(record);
    const backupSource = article.querySelector('[data-field="backup-source"]');
    if (record.backupUrl) {
      backupSource.href = record.backupUrl;
      backupSource.hidden = false;
    }
    article.querySelector('[data-field="limits"]').textContent = record.specificLimits;
    article.querySelector('[data-field="original-title"]').textContent = record.title;
    article.querySelector('[data-field="integrity"]').textContent = catalogStatus(record);

    const isOpen = state.openId === record.id;
    detail.hidden = !isOpen;
    detailTrigger.setAttribute("aria-expanded", String(isOpen));
    detailTrigger.querySelector("span").textContent = isOpen ? "Close details" : "View details";
    detailTrigger.addEventListener("click", () => toggleRecord(record.id, true));

    article.style.setProperty("--vl-ir-row-index", index);
    return fragment;
  }

  function render() {
    applyFilters();
    const visibleRecords = state.filtered.slice(0, state.visible);
    const fragment = document.createDocumentFragment();
    visibleRecords.forEach((record, index) => fragment.append(renderRecord(record, index)));
    list.replaceChildren(fragment);

    const count = state.filtered.length;
    const shown = visibleRecords.length;
    resultCount.textContent = count;
    resultUnit.textContent = count === 1 ? "publication" : "publications";
    resultDescription.textContent = count
      ? `Showing ${shown} of ${count} matching ${count === 1 ? "publication" : "publications"}.`
      : "No publications match the current filters.";
    loadMore.hidden = shown >= count || count === 0;
    emptyState.hidden = count !== 0;
    clearFilters.hidden = !hasActiveFilters();

    if (state.openId && !visibleRecords.some((record) => record.id === state.openId)) {
      state.openId = null;
    }
  }

  function toggleRecord(id, updateUrl) {
    const restoreFocus = document.activeElement?.classList.contains("vl-ir-detail-trigger");
    state.openId = state.openId === id ? null : id;
    render();

    if (restoreFocus) {
      root.querySelector(`#paper-${CSS.escape(id)} .vl-ir-detail-trigger`)?.focus({ preventScroll: true });
    }

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.hash = state.openId ? `paper-${state.openId}` : "research-library";
      history.pushState({ openId: state.openId }, "", url);
    }

    if (state.openId) {
      requestAnimationFrame(() => {
        const article = root.querySelector(`#paper-${CSS.escape(state.openId)}`);
        article?.scrollIntoView({ behavior: scrollBehavior(), block: "center" });
      });
    }
  }

  function hasActiveFilters() {
    return state.ingredient !== "all" || state.model !== "all" || state.sort !== "catalog";
  }

  function resetCatalog() {
    state.ingredient = "all";
    state.model = "all";
    state.sort = "catalog";
    state.visible = 10;
    state.openId = null;
    selectControllers.model?.setValue("all");
    selectControllers.sort?.setValue("catalog");
    setIngredientButton("all");
    render();
    replaceCatalogHash();
  }

  function closeAllCatalogSelects(except) {
    Object.values(selectControllers).forEach((controller) => {
      if (controller !== except) controller.close();
    });
  }

  function setupCatalogSelect(root) {
    const key = root.dataset.catalogSelect;
    const trigger = root.querySelector(".vl-ir-select__trigger");
    const valueNode = trigger.querySelector(":scope > span");
    const menu = root.querySelector(".vl-ir-select__menu");
    const options = [...menu.querySelectorAll('[role="option"]')];

    function selectedOption() {
      return options.find((option) => option.getAttribute("aria-selected") === "true") || options[0];
    }

    function setOptionTabStop(activeOption) {
      options.forEach((option) => {
        option.tabIndex = option === activeOption ? 0 : -1;
      });
    }

    function close({ restoreFocus = false } = {}) {
      root.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      menu.setAttribute("aria-hidden", "true");
      options.forEach((option) => {
        option.tabIndex = -1;
      });
      if (restoreFocus) trigger.focus({ preventScroll: true });
    }

    function open(focusTarget = selectedOption()) {
      closeAllCatalogSelects(controller);
      root.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
      menu.setAttribute("aria-hidden", "false");
      setOptionTabStop(focusTarget);
      requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
    }

    function setValue(value) {
      const option = options.find((item) => item.dataset.value === value) || options[0];
      options.forEach((item) => item.setAttribute("aria-selected", String(item === option)));
      valueNode.textContent = option.querySelector("span").textContent;
      return option.dataset.value;
    }

    function choose(option) {
      const nextValue = setValue(option.dataset.value);
      state[key] = nextValue;
      state.visible = 10;
      state.openId = null;
      replaceCatalogHash();
      close({ restoreFocus: true });
      render();
    }

    function moveFocus(current, delta) {
      const currentIndex = Math.max(0, options.indexOf(current));
      const nextIndex = (currentIndex + delta + options.length) % options.length;
      setOptionTabStop(options[nextIndex]);
      options[nextIndex].focus();
    }

    const controller = { close, open, setValue, root };

    trigger.addEventListener("click", () => {
      if (root.classList.contains("is-open")) close();
      else open();
    });

    trigger.addEventListener("keydown", (event) => {
      if (!["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) return;
      event.preventDefault();
      const target = event.key === "ArrowUp" ? options.at(-1) : selectedOption();
      open(target);
    });

    menu.addEventListener("click", (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) choose(option);
    });

    menu.addEventListener("keydown", (event) => {
      const option = event.target.closest('[role="option"]');
      if (!option) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        moveFocus(option, event.key === "ArrowDown" ? 1 : -1);
      } else if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        const target = event.key === "Home" ? options[0] : options.at(-1);
        setOptionTabStop(target);
        target.focus();
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        choose(option);
      } else if (event.key === "Escape") {
        event.preventDefault();
        close({ restoreFocus: true });
      } else if (event.key === "Tab") {
        event.preventDefault();
        close();
        if (event.shiftKey) {
          trigger.focus();
        } else if (key === "model") {
          selectControllers.sort.root.querySelector(".vl-ir-select__trigger").focus();
        } else {
          methodTrigger.focus();
        }
      }
    });

    selectControllers[key] = controller;
    setValue(state[key]);
    return controller;
  }

  function setIngredientButton(key) {
    const buttons = [...ingredientFilter.querySelectorAll("button[data-ingredient]")];
    const index = Math.max(0, buttons.findIndex((button) => button.dataset.ingredient === key));
    ingredientFilter.style.setProperty("--vl-ir-filter-index", index);
    buttons.forEach((button) => {
      const selected = button.dataset.ingredient === key;
      button.setAttribute("aria-checked", String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
  }

  function restoreRecordFromHash({ scroll = true } = {}) {
    const match = window.location.hash.match(/^#paper-(.+)$/);
    if (!match) {
      state.openId = null;
      render();
      return;
    }
    const id = decodeURIComponent(match[1]);
    const record = state.records.find((item) => item.id === id);
    if (!record) {
      state.openId = null;
      render();
      return;
    }

    state.openId = id;
    state.ingredient = "all";
    state.model = "all";
    setIngredientButton("all");
    selectControllers.model?.setValue("all");
    applyFilters();
    state.visible = Math.max(10, state.filtered.findIndex((item) => item.id === id) + 1);
    render();
    if (scroll) {
      requestAnimationFrame(() =>
        root
          .querySelector(`#paper-${CSS.escape(id)}`)
          ?.scrollIntoView({ behavior: scrollBehavior(), block: "center" }),
      );
    }
  }

  ingredientFilter.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-ingredient]");
    if (!button) return;
    state.ingredient = button.dataset.ingredient;
    state.visible = 10;
    state.openId = null;
    replaceCatalogHash();
    setIngredientButton(state.ingredient);
    render();
  });

  ingredientFilter.addEventListener("keydown", (event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const buttons = [...ingredientFilter.querySelectorAll("button[data-ingredient]")];
    const current = buttons.findIndex((button) => button.getAttribute("aria-checked") === "true");
    let next = current;
    if (event.key === "ArrowRight") next = (current + 1) % buttons.length;
    if (event.key === "ArrowLeft") next = (current - 1 + buttons.length) % buttons.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = buttons.length - 1;
    event.preventDefault();
    buttons[next].focus();
    buttons[next].click();
  });

  root.querySelectorAll("[data-catalog-select]").forEach(setupCatalogSelect);

  document.addEventListener("pointerdown", (event) => {
    Object.values(selectControllers).forEach((controller) => {
      if (!controller.root.contains(event.target)) controller.close();
    });
  });

  loadMore.addEventListener("click", () => {
    state.visible += 10;
    render();
  });

  clearFilters.addEventListener("click", resetCatalog);
  resetButtons.forEach((button) => button.addEventListener("click", resetCatalog));

  methodTrigger.addEventListener("click", () => {
    const expanded = methodTrigger.getAttribute("aria-expanded") === "true";
    methodTrigger.setAttribute("aria-expanded", String(!expanded));
    methodPanel.hidden = expanded;
  });

  window.addEventListener("popstate", () => restoreRecordFromHash());

  function validateCatalog(data) {
    const records = Array.isArray(data?.records) ? data.records : [];
    const counts = data?.counts;
    const requiredTextFields = [
      "id",
      "ingredient",
      "title",
      "authors",
      "journal",
      "sourceUrl",
      "sourceType",
      "studyType",
      "speciesOrSystem",
      "materialStudied",
      "focus",
      "direction",
      "summary",
      "specificLimits",
      "integrityStatus",
      "verifiedAsOf",
    ];
    const allowedIngredients = new Set(["Ginseng", "Lion's mane", "Whole sesame oil"]);
    const allowedStudyTypes = new Set(["animal", "cell_or_ex_vivo"]);
    const allowedDirections = new Set([
      "positive_as_reported",
      "positive_as_reported_with_dose_nuance",
      "positive_as_reported_with_endpoint_nuance",
      "mixed_positive_inflammation_no_colonization_effect",
      "mixed_internally_inconsistent",
      "mixed_unclear",
      "unclear_unverified",
    ]);
    const seenIds = new Set();
    const ingredientCount = (ingredient) => records.filter((record) => record.ingredient === ingredient).length;
    const modelCount = (studyType) => records.filter((record) => record.studyType === studyType).length;
    const validRecords = records.every((record) => {
      const id = safeText(record.id);
      const uniqueId = !seenIds.has(id);
      seenIds.add(id);
      return (
        requiredTextFields.every(
          (field) => typeof record[field] === "string" && safeText(record[field]),
        ) &&
        uniqueId &&
        /^[GHS]-\d{2}$/.test(id) &&
        allowedIngredients.has(record.ingredient) &&
        allowedStudyTypes.has(record.studyType) &&
        allowedDirections.has(record.direction) &&
        Number.isInteger(record.year) &&
        record.year >= 1900 &&
        record.year <= 2026 &&
        record.verifiedAsOf === data?.verifiedAsOf &&
        /^https?:\/\//.test(record.sourceUrl) &&
        (!record.backupUrl || /^https?:\/\//.test(record.backupUrl)) &&
        (!record.doi || typeof record.doi === "string")
      );
    });

    const isValid =
      data?.schemaVersion === "1.0" &&
      data?.verifiedAsOf === "2026-07-16" &&
      records.length === 50 &&
      counts?.total === 50 &&
      counts?.byIngredient?.ginseng === 32 &&
      counts?.byIngredient?.lionsMane === 12 &&
      counts?.byIngredient?.wholeSesameOil === 6 &&
      counts?.byStudyType?.animal === 43 &&
      counts?.byStudyType?.cellOrExVivo === 7 &&
      counts?.byStudyType?.human === 0 &&
      ingredientCount("Ginseng") === 32 &&
      ingredientCount("Lion's mane") === 12 &&
      ingredientCount("Whole sesame oil") === 6 &&
      modelCount("animal") === 43 &&
      modelCount("cell_or_ex_vivo") === 7 &&
      validRecords;

    if (!isValid) throw new Error("The frozen research catalog failed integrity validation.");
    return data;
  }

  function showCatalogError() {
    root.dataset.catalogError = "true";
    toolbar.inert = true;
    toolbar.hidden = true;
    toolbar.setAttribute("aria-hidden", "true");
    methodTrigger.hidden = true;
    methodTrigger.setAttribute("aria-expanded", "false");
    methodPanel.hidden = true;
    loadMore.disabled = true;
    loadMore.hidden = true;
    loadMoreWrap.hidden = true;
    clearFilters.hidden = true;
    catalogBiasNote.hidden = true;
    resetButtons.forEach((button) => {
      button.disabled = true;
      button.hidden = true;
    });
    list.replaceChildren();
    resultCount.textContent = "Research catalog";
    resultUnit.textContent = "";
    resultDescription.textContent = "The research catalog could not be loaded.";
    emptyState.hidden = false;
    emptyState.querySelector(".vl-ir-eyebrow").textContent = "Catalog unavailable";
    emptyState.querySelector("h3").textContent = "Please reload the page. If this continues, contact Vitality Lab.";
  }

  async function init() {
    try {
      const response = await fetch(root.dataset.catalogUrl);
      if (!response.ok) throw new Error(`Research data returned ${response.status}`);
      const data = validateCatalog(await response.json());
      state.records = data.records;
      render();
      restoreRecordFromHash({ scroll: false });
      root.dataset.catalogLoaded = "true";
      window.__GF1R_LIBRARY__ = { state, resetCatalog };
    } catch (error) {
      console.error(error);
      showCatalogError();
    }
  }

  init();
})();
